// Canonical capabilities and helpers for trace color behavior

export type ColorMode = 'categorical' | 'continuous' | 'none';

export interface TraceCapabilities {
  colorMode: ColorMode;
  supportsPaletteColorway: boolean;
  supportsPerTraceFill: boolean;
  supportsPerPointColors: boolean;
  supportsContinuousScale: boolean;
  supportsLine: boolean;
  supportsMarker: boolean;
  supportsMarkerSymbol: boolean;
  supportsWaterfallRoles: boolean;
  supportsFinancialRoles: boolean;
  supportsSliceColors: boolean;
  supportsNodeColors: boolean;
  supportsColorbar: boolean;
}

type PartialCaps = Partial<TraceCapabilities>;

const CONTINUOUS_TYPES = new Set([
  'heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'choropleth', 'choroplethmapbox', 'densitymapbox', 'image'
]);

const SLICE_TYPES = new Set(['pie', 'funnelarea', 'treemap', 'sunburst', 'icicle']);

export const TRACE_CAPS_DEFAULT: Record<string, PartialCaps> = {
  // Basic 2D Charts
  scatter: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsContinuousScale: false,
    supportsLine: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  scattergl: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsContinuousScale: false,
    supportsLine: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  bar: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsLine: true
  },
  histogram: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsMarker: true
  },
  box: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsMarker: true,
    supportsLine: true
  },
  violin: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsMarker: true,
    supportsLine: true
  },
  funnel: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsMarker: true
  },
  // Financial Charts
  candlestick: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsFinancialRoles: true
  },
  ohlc: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsFinancialRoles: true
  },
  waterfall: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsWaterfallRoles: true
  },
  // Pie/Slice Charts
  pie: {
    colorMode: 'categorical',
    supportsSliceColors: true
  },
  funnelarea: {
    colorMode: 'categorical',
    supportsSliceColors: true
  },
  // Hierarchical Charts
  treemap: {
    colorMode: 'categorical',
    supportsSliceColors: true
  },
  sunburst: {
    colorMode: 'categorical',
    supportsSliceColors: true
  },
  icicle: {
    colorMode: 'categorical',
    supportsSliceColors: true
  },
  // Network/Flow Charts
  sankey: {
    colorMode: 'categorical',
    supportsNodeColors: true
  },
  // 2D Density/Matrix Charts (Continuous)
  heatmap: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  contour: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  histogram2d: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  histogram2dcontour: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  image: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  // Geographic Charts
  choropleth: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  choroplethmapbox: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  densitymapbox: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true
  },
  scattergeo: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  scattermapbox: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  // Multi-dimensional Charts
  splom: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  parcoords: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true,
    supportsLine: true
  },
  parcats: {
    colorMode: 'continuous',
    supportsContinuousScale: true,
    supportsColorbar: true,
    supportsLine: true
  },
  // 3D Charts
  scatter3d: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  scatterpolar: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  scatterternary: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true,
    supportsPerPointColors: true,
    supportsMarker: true,
    supportsMarkerSymbol: true
  },
  // Indicator/Gauge/Bullet Charts
  indicator: {
    colorMode: 'categorical',
    supportsPaletteColorway: true,
    supportsPerTraceFill: true, // For gauge.bar.color
    supportsPerPointColors: false,
    supportsMarker: false,
    supportsLine: false
  }
};

function isNumericArray(arr: unknown): boolean {
  return Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'number' && isFinite(v));
}

function isStringArray(arr: unknown): boolean {
  return Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'string');
}

export function effectiveColorMode(trace: Record<string, unknown>): ColorMode {
  const type = String((trace as any)?.type || 'scatter').toLowerCase();
  if (CONTINUOUS_TYPES.has(type)) return 'continuous';

  const m = (trace as any)?.marker || {};
  const z = (trace as any)?.z;
  
  // If coloraxis is set, this is definitely a continuous scale trace
  if ((trace as any)?.coloraxis) return 'continuous';
  
  // If showscale is true, this is a continuous scale trace (colorbar indicates continuous)
  if ((trace as any)?.showscale === true) return 'continuous';
  
  // Check for z array early - heatmaps and other continuous plots always have z
  // Handle both 2D arrays (z is array of arrays) and flat arrays
  if (z !== undefined && z !== null) {
    if (Array.isArray(z)) {
      // Check if it's a 2D array (array of arrays) or a flat numeric array
      if (z.length > 0) {
        if (Array.isArray(z[0])) {
          // 2D array - definitely continuous
          return 'continuous';
        } else if (typeof z[0] === 'number' && isFinite(z[0])) {
          // Flat numeric array - also continuous
          return 'continuous';
        }
      }
    }
  }
  
  // For treemap/sunburst/icicle, check if marker.colors is numeric (continuous scale)
  // before defaulting to categorical
  if (SLICE_TYPES.has(type)) {
    if (isNumericArray(m?.colors) || m?.colorscale) {
      return 'continuous';
    }
    return 'categorical';
  }
  
  if (type === 'waterfall') return 'categorical';
  
  if (isNumericArray(m?.color) || typeof m?.color === 'number') return 'continuous';
  if ((trace as any)?.colorscale || m?.colorscale) return 'continuous';
  if (isStringArray(m?.color) || isStringArray(m?.colors)) return 'categorical';
  return 'categorical';
}

// Helper: explicit allow list for per-point editable colors
// Extended to include all marker-bearing traces that support per-point colors
export function supportsPerPointColors(trace: Record<string, unknown>): boolean {
  try {
    const type = String((trace as any)?.type || 'scatter').toLowerCase();
    // All scatter variants and bar charts support per-point colors
    const perPointTypes = new Set([
      'scatter',
      'scattergl',
      'scattergeo',
      'scattermapbox',
      'scatter3d',
      'scatterpolar',
      'scatterternary',
      'splom',
      'bar'
    ]);
    return perPointTypes.has(type);
  } catch {
    return false;
  }
}

export function getTraceCapabilities(trace: Record<string, unknown>): TraceCapabilities {
  const type = String((trace as any)?.type || 'scatter').toLowerCase();
  const base: PartialCaps = TRACE_CAPS_DEFAULT[type] || TRACE_CAPS_DEFAULT['scatter'] || {};
  const mode = effectiveColorMode(trace);

  // Build full caps with reasonable defaults
  const caps: TraceCapabilities = {
    colorMode: mode,
    supportsPaletteColorway: !!base.supportsPaletteColorway,
    supportsPerTraceFill: !!base.supportsPerTraceFill,
    supportsPerPointColors: !!base.supportsPerPointColors,
    supportsContinuousScale: !!base.supportsContinuousScale || mode === 'continuous',
    supportsLine: !!base.supportsLine,
    supportsMarker: !!base.supportsMarker,
    supportsMarkerSymbol: !!base.supportsMarkerSymbol,
    supportsWaterfallRoles: !!base.supportsWaterfallRoles,
    supportsFinancialRoles: !!base.supportsFinancialRoles,
    supportsSliceColors: !!base.supportsSliceColors,
    supportsNodeColors: !!base.supportsNodeColors,
    supportsColorbar: !!base.supportsColorbar || mode === 'continuous'
  };

  return caps;
}

// Small helper to union capabilities across traces
export function unionCapabilities(traces: Array<Record<string, unknown>>): {
  hasCategorical: boolean;
  hasContinuous: boolean;
  hasColorbar: boolean;
  hasSliceTypes: boolean;
  hasPerPointColors: boolean;
} {
  return traces.reduce((acc, tr) => {
    const caps = getTraceCapabilities(tr);
    acc.hasCategorical = acc.hasCategorical || caps.colorMode === 'categorical';
    acc.hasContinuous = acc.hasContinuous || caps.colorMode === 'continuous';
    acc.hasColorbar = acc.hasColorbar || caps.supportsColorbar;
    acc.hasSliceTypes = acc.hasSliceTypes || caps.supportsSliceColors;
    acc.hasPerPointColors = acc.hasPerPointColors || supportsPerPointColors(tr);
    return acc;
  }, { hasCategorical: false, hasContinuous: false, hasColorbar: false, hasSliceTypes: false, hasPerPointColors: false });
}

// Expose internals for tests
export const __test = { isNumericArray, isStringArray };


// Estimate categorical cardinality across traces and layout
// Uses theme mappings domain when present; otherwise inspects trace-level labels or categorical axes
export function getCategoricalCardinality(traces: Array<Record<string, unknown>> = [], layout?: any): number {
  try {
    // 1) Prefer explicit theme domain when available
    const theme:any = (((layout || {})?.meta || {}) as any)?.lumi?.theme || {};
    const domain: any[] = Array.isArray((theme as any)?.mappings?.domain) ? (theme as any).mappings.domain : [];
    if (Array.isArray(domain) && domain.length > 0) return domain.length;

    // 2) Aggregate potential category counts per trace
    let maxCategories = 0;
    const addMax = (n: number) => { if (Number.isFinite(n)) maxCategories = Math.max(maxCategories, n); };

    for (const tr of traces) {
      if (!tr) continue;
      const type = String((tr as any)?.type || 'scatter').toLowerCase();

      // Pie-like traces: categories across slices
      if (type === 'pie' || type === 'funnelarea') {
        const labels = (tr as any)?.labels || (tr as any)?.label;
        if (Array.isArray(labels)) addMax(labels.length);
        continue;
      }

      // Hierarchical slices
      if (type === 'treemap' || type === 'sunburst' || type === 'icicle') {
        const labels = (tr as any)?.labels || (tr as any)?.label;
        if (Array.isArray(labels)) addMax(labels.length);
        continue;
      }

      // Bar-like: check x/y string arrays
      if (type === 'bar' || type === 'histogram' || type === 'funnel' || type === 'waterfall') {
        const x = (tr as any)?.x; const y = (tr as any)?.y;
        const uniq = (arr: any) => Array.isArray(arr) ? new Set(arr.map((v:any)=>String(v))).size : 0;
        addMax(Math.max(uniq(x), uniq(y)));
        continue;
      }

      // Scatter-like: count distinct names as categories when present
      if (type === 'scatter' || type === 'scattergl' || type === 'line' || type === 'splom') {
        // name-based series categories handled outside via visible traces; skip here
        continue;
      }
    }

    // 3) Fallback to number of unique visible trace names
    if (maxCategories === 0) {
      const names = new Set<string>();
      for (const tr of traces) {
        const vis = (tr as any)?.visible;
        if (vis === false || String(vis).toLowerCase() === 'legendonly' || String(vis).toLowerCase() === 'legendonly') continue;
        if ((tr as any)?.name) names.add(String((tr as any).name));
      }
      if (names.size > 0) maxCategories = names.size;
    }

    return maxCategories;
  } catch {
    return 0;
  }
}

